import request from '@/utils/request';

/**
 * 平台公开配置（GET /api/v2/common/platform_config）— 免登录
 * @returns {Promise<{ platform: { name, logo_url, copyright, seo_title, ... }, customer_service: { contact: string } }>}
 */
export function fetchPlatformConfig() {
  return request({
    url: '/v2/common/platform_config',
    method: 'get'
  });
}

/** 协议配置（Apifox：GET /api/v2/common/agreement_list） */
export function fetchAgreementList() {
  return request({
    url: '/v2/common/agreement_list',
    method: 'get'
  });
}

/** 学校 PC 资讯投放端口（后端按 token.role 或 port 过滤 visible_ports） */
export const INFORMATION_PORT_SCHOOL_ADMIN = 'school_admin';

/**
 * 资讯列表（GET /api/v2/common/information_list）
 * 学校 PC 登录态携带 token 即可；无 token 时需传 port=school_admin。
 * @param {{ page?: number, limit?: number, keyword?: string, port?: string }} params
 */
export function fetchInformationList(params = {}) {
  const q = {
    page: params.page != null ? params.page : 1,
    limit: params.limit != null ? params.limit : 15,
    keyword: params.keyword || undefined
  };
  if (params.port) q.port = params.port;
  return request({
    url: '/v2/common/information_list',
    method: 'get',
    params: q
  });
}

/**
 * 资讯详情（GET /api/v2/common/information_detail）
 * @param {number|string} id
 * @param {{ port?: string }} [opts]
 */
export function fetchInformationDetail(id, opts) {
  const q = { id: String(id) };
  if (opts && opts.port) q.port = opts.port;
  return request({
    url: '/v2/common/information_detail',
    method: 'get',
    params: q
  });
}

/** 教练认证相关选项（GET /api/v2/common/teacher_certify_options）— 含 sport_projects、cert_documents */
export function fetchTeacherCertifyOptions() {
  return request({
    url: '/v2/common/teacher_certify_options',
    method: 'get'
  });
}

/**
 * 社团课课程列表（GET /api/v2/common/course_list）
 * 固定传 `type=2`（社团课），与校本课 type=3 等区分。
 * @param {{ page: number, limit: number, exercise_dict_data_id?: string|number, price_sort?: 'asc'|'desc' }} params
 */
export function fetchClubCourseList(params) {
  const q = {
    page: params.page,
    limit: params.limit,
    type: 2
  };
  const eid = params.exercise_dict_data_id;
  if (eid !== undefined && eid !== null && eid !== '') {
    const s = String(eid).trim();
    if (s) q.exercise_dict_data_id = s;
  }
  const ps = params && params.price_sort;
  if (ps === 'asc' || ps === 'desc') q.price_sort = ps;
  return request({
    url: '/v2/common/course_list',
    method: 'get',
    params: q
  });
}

/** 社团课课程详情（GET /api/v2/common/course_detail?id=） */
export function fetchClubCourseDetail(id) {
  return request({
    url: '/v2/common/course_detail',
    method: 'get',
    params: { id: String(id) }
  });
}

/**
 * 通用上传（POST /api/v2/common/upload）
 * @param {File|Blob} file
 * @param {string} [fieldName] 表单字段名，默认 file
 * @returns {Promise<*>} 解包后的 data，一般为路径字符串或含 url/path 的对象
 */
export function uploadCommonFile(file, fieldName = 'file') {
  const fd = new FormData();
  fd.append(fieldName, file);
  return request({
    url: '/v2/common/upload',
    method: 'post',
    data: fd
  });
}

/**
 * 课时教案/排期详情（GET /api/v2/common/plan_detail）
 * @param {number|string} scheduleId 排期 ID（日历列表 schedule_id）
 */
/** plan_detail 业务 404「暂无教案」：排期存在但尚未上传教案，非排期不存在 */
export function isPlanDetailEmptyError(err) {
  const data = err && err.response && err.response.data;
  if (!data || typeof data !== 'object') return false;
  const code = Number(data.code);
  const msg = String(data.msg || data.message || '');
  return code === 404 || msg.includes('暂无教案');
}

export function fetchPlanDetail(scheduleId) {
  return request({
    url: '/v2/common/plan_detail',
    method: 'get',
    params: { schedule_id: String(scheduleId) },
    skipErrorMessage: true
  });
}

/** 消息中心摘要 GET /api/v2/common/message_center_summary */
export function fetchMessageCenterSummary() {
  return request({
    url: '/v2/common/message_center_summary',
    method: 'get'
  });
}

/**
 * 消息列表 GET /api/v2/common/message_list
 * @param {{ category?: string, msg_types?: number|string, page?: number, limit?: number }} params
 */
export function fetchMessageList(params) {
  const q = {
    page: params.page != null ? params.page : 1,
    limit: params.limit != null ? params.limit : 15
  };
  if (params.msg_types != null && params.msg_types !== '') {
    q.msg_types = params.msg_types;
  } else if (params.category) {
    q.category = params.category;
  }
  return request({
    url: '/v2/common/message_list',
    method: 'get',
    params: q
  });
}

/**
 * 消息详情 GET /api/v2/common/message_detail
 * @param {number|string} id
 * @param {{ markRead?: boolean }} [opts]
 */
export function fetchMessageDetail(id, opts) {
  const q = { id: String(id) };
  if (opts && opts.markRead) q.mark_read = 1;
  return request({
    url: '/v2/common/message_detail',
    method: 'get',
    params: q
  });
}

/** 标记已读 POST /api/v2/common/read_message（单条 id 或 category+all=1，旧接口，优先用 markMessagesRead） */
export function readMessage(payload) {
  const body = new URLSearchParams();
  if (typeof payload === 'string' || typeof payload === 'number') {
    body.append('id', String(payload));
  } else if (payload && payload.id != null && payload.id !== '') {
    body.append('id', String(payload.id));
  } else if (payload && payload.category) {
    body.append('category', String(payload.category));
    body.append('all', String(payload.all != null ? payload.all : 1));
  }
  return request({
    url: '/v2/common/read_message',
    method: 'post',
    data: body.toString(),
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }
  });
}

/**
 * 批量 / 分类全部标为已读 POST /api/v2/common/message_mark_read
 * @param {{ ids?: Array<number|string>, category?: string, all?: 0|1 }} payload
 */
export function markMessagesRead(payload = {}) {
  const body = {};
  if (payload.all === 1 || payload.all === '1') {
    body.all = 1;
    body.category = String(payload.category || '');
  } else {
    const ids = (Array.isArray(payload.ids) ? payload.ids : [])
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    body.ids = ids;
  }
  return request({
    url: '/v2/common/message_mark_read',
    method: 'post',
    data: body
  });
}

/**
 * 批量删除（软删）POST /api/v2/common/message_delete
 * @param {Array<number|string>} ids
 */
export function deleteMessages(ids) {
  const idList = (Array.isArray(ids) ? ids : [])
    .map((id) => Number(id))
    .filter((n) => Number.isFinite(n) && n > 0);
  return request({
    url: '/v2/common/message_delete',
    method: 'post',
    data: { ids: idList }
  });
}

/** 未读数 GET /api/v2/common/message_unread_count */
export function fetchMessageUnreadCount() {
  return request({
    url: '/v2/common/message_unread_count',
    method: 'get'
  });
}

/**
 * 官方通知列表 GET /api/v2/common/platform_notice_list
 * @param {{ page?: number, limit?: number }} params
 */
export function fetchPlatformNoticeList(params = {}) {
  return request({
    url: '/v2/common/platform_notice_list',
    method: 'get',
    params: {
      page: params.page != null ? params.page : 1,
      limit: params.limit != null ? params.limit : 15
    }
  });
}

/** 从上传接口返回中取出可存库的路径（兼容多种字段） */
export function pickUploadedPath(data) {
  if (data == null) return '';
  if (typeof data === 'string') return data.trim();
  return (
    data.url ||
    data.path ||
    data.file ||
    data.filepath ||
    data.src ||
    (data.data && (typeof data.data === 'string' ? data.data : pickUploadedPath(data.data))) ||
    ''
  );
}
